import './ConsentsPublicPage.scss';

import axios from 'axios';
import { marked } from 'marked';
import { useEffect, useMemo, useState } from 'react';
import { Spinner, Stack } from 'react-bootstrap';
import { useLocation } from 'react-router';

import { APP_SETTINGS } from '@constants/constants';
import { PublicConsent } from '@customTypes/consent';

function parseConsentIds(search: string): string[] {
  const sp = new URLSearchParams(search);
  const segments = sp.getAll('consents').flatMap(part =>
    part
      .split(',')
      .map(s => s.trim())
      .filter(Boolean),
  );
  return Array.from(new Set(segments));
}

async function fetchPublicConsents(consentIds: string[]): Promise<PublicConsent[]> {
  const { data } = await axios.get<PublicConsent[]>(`${APP_SETTINGS.apiurl}/consent`, {
    params: { consents: consentIds.join(',') },
  });
  return data;
}

export default function ConsentsPublicPage() {
  const { search } = useLocation();
  const consentIds = useMemo(() => parseConsentIds(search), [search]);

  const [consents, setConsents] = useState<PublicConsent[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (consentIds.length === 0) {
      setConsents(null);
      setError(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    fetchPublicConsents(consentIds)
      .then(data => {
        if (!cancelled) {
          setConsents(Array.isArray(data) ? data : []);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setError('Unable to load consent documents. Please try again later.');
          setConsents(null);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [consentIds]);

  return (
    <div className='consents-public'>
      {consentIds.length === 0 && (
        <p className='text-muted mb-0'>
          No consents were specified. Add a <code className='fs-6'>consents</code> query parameter.
        </p>
      )}

      {consentIds.length > 0 && loading && (
        <div className='d-flex justify-content-center py-5'>
          <Spinner animation='border' role='status' variant='primary'>
            <span className='visually-hidden'>Loading…</span>
          </Spinner>
        </div>
      )}

      {error && <p className='text-danger mb-0'>{error}</p>}

      {!loading && !error && consents && consents.length === 0 && consentIds.length > 0 && (
        <p className='text-muted mb-0'>No matching consent documents were returned.</p>
      )}

      {!loading && !error && consents && consents.length > 0 && (
        <Stack gap={4}>
          {consents.map(c => (
            <div
              key={c.consent_uri || c.name}
              className='consent-markdown'
              // Consent HTML is produced from API markdown (same pattern as patients-portal settings).
              dangerouslySetInnerHTML={{ __html: marked.parse(c.consent_text || '') }}
            />
          ))}
        </Stack>
      )}
    </div>
  );
}
